import { fetchBaseQuery } from "@reduxjs/toolkit/query"

import type { RootState } from "@/store/store"

import { SelectorArgumentsFor } from "./types"

export const BASE_URL = "https://api.sort-me.org"

type SchemaT<Keys extends string, Args extends Record<Keys, unknown>> = {
  [k in Keys]?: (arg: Args[k]) => string
}

export function withQueryParams<
  Keys extends string,
  Args extends Partial<Record<Keys, unknown>> = Partial<Record<Keys, unknown>>,
>({
  url,
  params,
  schema = {},
}: {
  url: string
  params: Args
  schema?: SchemaT<Keys, Args>
}): string {
  const query = Object.entries(params)
    .filter(([_, value]) => value !== undefined)
    .map(([key, value]) => {
      const fn = schema[key as Keys] ?? String
      return `${key}=${fn(value as Args[Keys])}`
    })
    .join("&")

  return `${url}?${query}`
}

export const sortMeBaseQuery = fetchBaseQuery({
  baseUrl: BASE_URL,
  prepareHeaders: (headers, api) => {
    const {
      user: { token },
      common: { lang },
    } = api.getState() as RootState

    const next = new Headers(headers)

    if (token) next.set("Authorization", `Bearer ${token}`)

    next.set("X-Language", lang ?? "ru")

    return next
  },
})

export const sortMeBetaBaseQuery = fetchBaseQuery({
  baseUrl: "https://beta.sort-me.org",
  prepareHeaders: (headers, api) => {
    const {
      user: { token },
      common: { lang },
    } = api.getState() as RootState

    const next = new Headers(headers)

    if (token) next.set("Authorization", `Bearer ${token}`)

    next.set("X-Language", lang ?? "ru")

    return next
  },
})

// FIXME: Temporary. Probably should keep these in Redux slice
export function getPreferredLanguages() {
  return ["ru", "en"]
}

export const ToQueryBoolean = (x: boolean) => (x ? "1" : "0")
export const ToQueryArray = (x: unknown[]) => x.map(String).join(",")

export const createQuerySelector = <Hook, R>(
  _: Hook,
  fn: (_: SelectorArgumentsFor<Hook>) => R,
): typeof fn => fn
